// Persönliche Wahl, die der Server beachtet: wie lange Chat-Verläufe bleiben
// und ob Neuigkeiten per Mail kommen. Jeder Benutzer entscheidet für sich.
import { useEffect, useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { meldungZeigen } from '../../../gemeinsam/komponenten/Meldungen';
import { Hinweis, Karte, KartenKopf } from '../../../gemeinsam/komponenten/Basis';

type Stand = {
  chatAufbewahrungTage: number; chatAufbewahrungEigeneWahl: boolean;
  chatStufen: number[]; neuigkeitenPerMail: boolean;
};

const stufeText = (t: number) => (t === 0 ? 'Nie löschen' : `${t} Tage`);

export function Privatsphaere() {
  const [stand, setStand] = useState<Stand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    api<Stand>('/settings/privat').then(setStand).catch(() => setFehler('Einstellungen konnten nicht geladen werden.'));
  }, []);

  async function speichern(daten: Partial<Pick<Stand, 'chatAufbewahrungTage' | 'neuigkeitenPerMail'>>, text: string) {
    setFehler(null);
    try {
      setStand(await api<Stand>('/settings/privat', { methode: 'PUT', daten }));
      meldungZeigen(text);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Konnte nicht gespeichert werden.');
    }
  }

  return (
    <Karte>
      <KartenKopf titel="Chat & Benachrichtigungen" hinweis="Gilt nur für Sie — jeder im Team wählt selbst." />
      <div className="space-y-5 p-5">
        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}

        <div>
          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Chat-Verläufe automatisch löschen</p>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Team-Chat und KI-Assistent. Ältere Nachrichten sehen Sie nicht mehr; endgültig gelöscht werden sie,
            sobald auch Ihr Gesprächspartner sie nicht mehr aufbewahrt — samt Anhängen.
          </p>
          <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-label="Chat-Verläufe löschen nach">
            {(stand?.chatStufen ?? [7, 20, 30, 50, 90, 0]).map(t => {
              const an = stand?.chatAufbewahrungTage === t;
              return (
                <button key={t} type="button" role="radio" aria-checked={an} disabled={!stand}
                        onClick={() => !an && speichern({ chatAufbewahrungTage: t },
                          t === 0 ? 'Chat-Verläufe werden nicht mehr automatisch gelöscht.' : `Chat-Verläufe werden nach ${t} Tagen gelöscht.`)}
                        className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition ${an
                          ? 'border-marke-600 bg-marke-600 text-white'
                          : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'}`}>
                  {stufeText(t)}{t === 30 && ' (Standard)'}
                </button>
              );
            })}
          </div>
          {stand && !stand.chatAufbewahrungEigeneWahl && (
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              Nachrichten von vor dem 27.10.2026 zählen ab diesem Tag — es geht nichts über Nacht verloren.
            </p>
          )}
        </div>

        <label className="flex items-start gap-3 border-t border-slate-200 pt-4 dark:border-slate-800">
          <input type="checkbox" className="mt-1" checked={stand?.neuigkeitenPerMail ?? true} disabled={!stand}
                 onChange={e => speichern({ neuigkeitenPerMail: e.target.checked },
                   e.target.checked ? 'Sie bekommen Neuigkeiten wieder per E-Mail.' : 'Keine Neuigkeiten mehr per E-Mail.')} />
          <span>
            <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">Neuigkeiten per E-Mail</span>
            <span className="block text-xs text-slate-500 dark:text-slate-400">
              Nach jedem größeren Update eine kurze Mail, was neu ist. In der App sehen Sie es beim nächsten Anmelden ohnehin.
            </span>
          </span>
        </label>
      </div>
    </Karte>
  );
}
